// Date helpers. Task due dates are calendar dates (YYYY-MM-DD) in the member's time zone, so most math is on plain dates.

export const WEEKDAYS = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'] as const;
export const MONTHS = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'] as const;
const MONTHS_SHORT = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'] as const;

/** Today's date (YYYY-MM-DD) in a time zone. */
export function todayIn(timeZone: string, now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export function parseDate(d: string): Date {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, day!));
}

export function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDays(d: string, n: number): string {
  const date = parseDate(d);
  date.setUTCDate(date.getUTCDate() + n);
  return formatDate(date);
}

export function diffDays(a: string, b: string): number {
  return Math.round((parseDate(a).getTime() - parseDate(b).getTime()) / 86_400_000);
}

export const weekday = (d: string) => parseDate(d).getUTCDay();

/** "Oggi", "Domani", "Ieri", "venerdì", "12 set", "12 set 2027" (+ " · 15:30"). */
export function formatDue(dueDate: string, dueTime: string | null, today: string): string {
  const diff = diffDays(dueDate, today);
  let label: string;
  if (diff === 0) label = 'Oggi';
  else if (diff === 1) label = 'Domani';
  else if (diff === -1) label = 'Ieri';
  else if (diff > 1 && diff < 7) label = WEEKDAYS[weekday(dueDate)]!;
  else {
    const d = parseDate(dueDate);
    label = `${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]}`;
    if (dueDate.slice(0, 4) !== today.slice(0, 4)) label += ` ${d.getUTCFullYear()}`;
  }
  return dueTime ? `${label} · ${dueTime}` : label;
}

/** "Venerdì 11 settembre" (only the first letter is capitalized, as in Italian). */
export function longDate(d: string): string {
  const date = parseDate(d);
  const s = `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Relative time for feeds: "ora", "5 min", "3 h", "ieri", "12 set". */
export function relativeTime(iso: string, now = Date.now()): string {
  const s = Math.max(0, (now - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'ora';
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  if (s < 172800) return 'ieri';
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

export function greeting(now = new Date()): string {
  const h = now.getHours();
  if (h < 5) return 'Buonanotte';
  if (h < 13) return 'Buongiorno';
  if (h < 18) return 'Buon pomeriggio';
  return 'Buonasera';
}
